import {useRef, useCallback, useMemo, useEffect} from "react"
import {
    calculateImageDimensions,
    calculateCenterPosition,
} from "../utils/calculationsUtils"
import {useCanvasRenderer} from "./useCanvasRenderer"
import {useDragAndDrop} from "./useDragAndDrop"
import {useImageLoad} from "./useImageLoad"
import {useVideoToggle} from "./useVideoToggle"
import {useZoom} from "./useZoom"
import {useModeSwitch} from "./useModeSwitch"
import {useCropper} from "./useCropper"

interface UseImageCropProps {
    materialIndex: number
}

export const useImageCrop = ({materialIndex}: UseImageCropProps) => {
    const cropper = useCropper(materialIndex)
    const mainCanvasRef = useRef<HTMLCanvasElement>(null)

    const prevPositionRef = useRef(cropper?.imageState.position)
    useEffect(() => {
        if (cropper) {
            prevPositionRef.current = cropper.imageState.position
        }
    })
    useCanvasRenderer({mainCanvasRef, cropper})

    const imageLoad = useImageLoad({materialIndex, cropper})

    const dragAndDrop = useDragAndDrop({cropper})

    useVideoToggle({cropper})

    const zoom = useZoom({canvasRef: mainCanvasRef, cropper})

    const modeSwitch = useModeSwitch({cropper})

    const resetImagePosition = useCallback(() => {
        if (
            !cropper ||
            cropper.imageState.status !== "loaded" ||
            !cropper.currentImageUrl
        )
            return

        const img = new Image()
        img.onload = () => {
            const dimensions = calculateImageDimensions(
                img,
                cropper.cropState.dimensions
            )
            const position = calculateCenterPosition(dimensions)

            cropper.setImageState({
                dimensions,
                position,
                zoom: 100,
            })

            const zoomFactor = 100 / 100
            cropper.setRenderData({
                position,
                dimensions: {
                    width: dimensions.width * zoomFactor,
                    height: dimensions.height * zoomFactor,
                },
            })
        }
        img.src = cropper.currentImageUrl
    }, [cropper])

    const handleDimensionChange = useCallback(
        (dim: "width" | "height", value: string) => {
            if (!cropper) return

            const newValue = Math.max(1, parseInt(value) || 0)
            const newDimensions = {
                ...cropper.cropState.dimensions,
                [dim]: newValue,
            }

            cropper.setCropDimensions(newDimensions)

            if (
                cropper.imageState.status === "loaded" &&
                cropper.currentImageUrl
            ) {
                const img = new Image()
                img.onload = () => {
                    const imgDimensions = calculateImageDimensions(
                        img,
                        newDimensions
                    )
                    const centerPosition =
                        calculateCenterPosition(imgDimensions)

                    cropper.setImageState({
                        dimensions: imgDimensions,
                        position: centerPosition,
                    })

                    const zoomFactor = cropper.imageState.zoom / 100
                    cropper.setRenderData({
                        position: centerPosition,
                        dimensions: {
                            width: imgDimensions.width * zoomFactor,
                            height: imgDimensions.height * zoomFactor,
                        },
                    })
                }
                img.src = cropper.currentImageUrl
            }
        },
        [cropper]
    )

    const handleImageUpload = useCallback(
        (file: File | undefined) => {
            if (!file) return
            imageLoad.handleImageUpload(file)
        },
        [imageLoad]
    )

    if (!cropper) {
        return {
            mainCanvasRef,
            handleImageUpload: () => console.warn("Cropper not ready"),
            handleDragStart: () => {},
            handleDrag: () => {},
            handleDragEnd: () => {},
            handleMouseLeave: () => {},
            handleDimensionChange: () => {},
            resetImagePosition: () => {},
            handleZoomChange: () => {},
            minZoom: 100,
            maxZoom: 500,
            step: 1,
            handleModeSwitch: () => {},
            imageState: {
                status: "initial" as const,
                dimensions: {width: 0, height: 0},
                position: {x: 0, y: 0},
                zoom: 100,
                videoPlaying: false,
            },
            cropState: {
                status: "idle" as const,
                isDragging: false,
                dimensions: {width: 0, height: 0},
                dragStart: {x: 0, y: 0},
            },
            mode: "crop",
            isProcessing: false,
            downloadUrl: null,
        }
    }

    return {
        mainCanvasRef,
        handleImageUpload,
        handleDragStart: dragAndDrop.handleDragStart,
        handleDrag: dragAndDrop.handleDrag,
        handleDragEnd: dragAndDrop.handleDragEnd,
        handleMouseLeave: dragAndDrop.handleMouseLeave,
        handleDimensionChange,
        resetImagePosition,
        handleZoomChange: zoom.handleZoomChange,
        minZoom: zoom.minZoom,
        maxZoom: zoom.maxZoom,
        step: zoom.step,
        handleModeSwitch: modeSwitch.handleModeSwitch,
        imageState: cropper.imageState,
        cropState: cropper.cropState,
        mode: cropper.mode,
        isProcessing: cropper.isProcessing,
        downloadUrl: cropper.downloadUrl,
    }
}
